import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { semesterWeek } from '@app/shared'

import {
  useAnnouncements,
  useAssignments,
  useClock,
  useCourses,
  useLectures,
  useSyncStatus,
  useTerm,
} from '../lib/data'
import { DataModeBadge } from '../components/HeaderBand'
import { SkeletonRows } from '../components/Card'
import { CalendarPlain } from '../components/school/icons'
import {
  CanvasSyncChip,
  SchoolBand,
  SchoolSearchField,
  Unavailable,
} from '../components/school/Chrome'
import { CourseCard } from '../components/school/CourseCard'
import { registerCourseCodes } from '../components/school/derive'
import { DeadlineRunway } from '../components/school/DeadlineRunway'
import { AnnouncementsCard } from '../components/school/AnnouncementList'

/**
 * SCHOOL OVERVIEW — docs/design/handoff/"LifeOS School.dc.html", option 6a.
 *
 * The state of four real courses in one view: a 2×2 grid of course cards, the merged two-week
 * deadline runway down the right, and the announcement feed under the grid.
 *
 * ABSENT BY DESIGN (README §3, and this is a rule rather than an omission): there is no waitlist
 * position anywhere on this screen, on the course page, or in the model behind them. The Canvas
 * API cannot supply one, so LifeOS never shows one — not blank, not "unknown", not at all. If a
 * future edit adds a field for it, that edit is the bug.
 *
 * The page does not scroll; the runway and the announcement feed own their own overflow, which
 * is what keeps the four course cards on screen at 900px.
 */
export function School() {
  const { data: clock } = useClock()
  const { data: term } = useTerm()
  const { data: courses, isPending: coursesPending, error: coursesError } = useCourses()
  // Every course gets its own colour, and the assignment has to be known before any card below
  // reads it — see components/school/derive.ts. Idempotent, so calling it on every render of the
  // one screen that always has the full roster costs nothing.
  if (courses) registerCourseCodes(courses.map((c) => c.code))
  const { data: lectures } = useLectures()
  const {
    data: openAssignments,
    isPending: assignmentsPending,
    error: assignmentsError,
  } = useAssignments({ openOnly: true })
  const {
    data: announcements,
    isPending: announcementsPending,
    error: announcementsError,
  } = useAnnouncements()
  const { data: sync, error: syncError } = useSyncStatus()

  const today = clock?.date
  const nowMinutes = clock?.minutesIntoDay ?? 0
  const week = today && term ? semesterWeek(today, term.instructionStarts) : 0
  // Canvas supplies no credit hours, so the total is only over the courses that HAVE one —
  // and when none does, the segment is dropped rather than printed as "0 units".
  const unitCounts = (courses ?? []).map((c) => c.units).filter((u): u is number => u !== null)
  const units = unitCounts.reduce((sum, u) => sum + u, 0)

  return (
    <>
      <SchoolBand>
        <div className="flex flex-none items-baseline gap-3 whitespace-nowrap">
          <span className="text-[17px] font-semibold leading-none tracking-[-0.01em]">
            {term?.name ?? 'School'}
          </span>
          {courses && (
            <span className="text-[12px] font-normal leading-none text-muted">
              {/* No term in remote mode until the Canvas connector lands, so no week label —
                  rather than a made-up one. Units and course count come from the courses read. */}
              {week > 0 ? `Week ${week} · ` : ''}
              {unitCounts.length > 0 ? `${units} units · ` : ''}
              {courses.length} courses
            </span>
          )}
        </div>

        {today && (
          <CanvasSyncChip
            rows={sync}
            error={syncError}
            today={today}
            nowMinutes={nowMinutes}
          />
        )}

        <SchoolSearchField placeholder="Search courses, lectures, files" maxWidth={320} />

        <div className="ml-auto flex flex-none items-center gap-3">
          {/* THE PHONE'S DOOR, on the desktop screen. The upload flow is built for a phone, but
              School is where the owner is when he remembers a lecture is still sitting in the
              recorder — and a route with no link into it is a route nobody finds. */}
          <Link
            to="/record"
            title="Upload a lecture recording from this device. It transcribes on your desktop PC."
            style={{ '--mo-rule': 'var(--color-school)' } as CSSProperties}
            className="mo-ink mo-press flex items-center gap-[9px] rounded-button border border-line bg-surface px-[13px] py-2 text-[11.5px] font-medium leading-none text-ink-2"
          >
            Upload a lecture
          </Link>
          <button
            type="button"
            disabled
            title="A week grid needs class meeting times, and Canvas carries none for Berkeley courses — they live in bCal. Deadlines alone would be a calendar with the classes missing."
            className="flex cursor-not-allowed items-center gap-[9px] rounded-button border border-line bg-surface px-[13px] py-2 text-[11.5px] font-medium leading-none text-ink-2"
          >
            <CalendarPlain size={14} strokeWidth={1.8} className="text-ink-2" />
            Week view
          </button>
          <DataModeBadge />
        </div>
      </SchoolBand>

      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden px-[30px] pb-[26px]">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-[14px]">
          {coursesPending && (
            <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
              <SkeletonRows rows={4} />
            </div>
          )}
          <Unavailable error={coursesError} />

          {courses && courses.length > 0 && today && (
            <div className="grid flex-none grid-cols-2 gap-[14px]">
              {courses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  term={term}
                  lectures={(lectures ?? []).filter((l) => l.courseId === course.id)}
                  // The next OPEN deadline, which is the first row of an already date-sorted,
                  // already open-only list. Submitted work is not a deadline.
                  nextDeadline={(openAssignments ?? []).find((a) => a.courseId === course.id)}
                  // Counted from the announcements themselves rather than read off the course
                  // row, so a badge can never disagree with the list under it.
                  unread={(announcements ?? []).filter((a) => a.courseId === course.id && !a.read).length}
                  today={today}
                  nowMinutes={nowMinutes}
                />
              ))}
            </div>
          )}

          {courses && courses.length === 0 && (
            <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
              <p className="text-body text-ink-3">
                Canvas returned no courses for this term. That is either a term with no enrolment
                or a connector that needs reconnecting — Settings → Connections says which.
              </p>
            </div>
          )}

          {today && (
            <AnnouncementsCard
              announcements={announcements}
              isPending={announcementsPending}
              error={announcementsError}
              today={today}
              nowMinutes={nowMinutes}
            />
          )}
        </div>

        {today && (
          <DeadlineRunway
            assignments={openAssignments}
            isPending={assignmentsPending}
            error={assignmentsError}
            today={today}
            nowMinutes={nowMinutes}
          />
        )}
      </div>
    </>
  )
}
